import { reactive, watch } from 'vue';

import mitt from 'mitt';
import _mergeWith from 'lodash/mergeWith';
import _isArray from 'lodash/isArray';
import _cloneDeep from 'lodash/cloneDeep';
import _uniq from 'lodash/uniq';

import { Accessor } from './Accessor';
import { IStore } from './runtime/IStore';
import { IOptions } from './runtime/IOptions';
import { Router } from './runtime/Router';
import { HttpService } from './runtime/HttpService';
import { Translations } from './runtime/Translations';
import { IRouteData } from './runtime/IRouteData';
import { CommandExecutor } from './runtime/CommandExecutor';
import { DefaultOptions } from './configuration/DefaultOptions';
import { ComponentName } from './configuration/components/ComponentName';
import { DataType } from './configuration/application/DataType';
import LoggerService from '@/common/services/LoggerService';
import {
  DetailDataTypeEnum,
  SearchDataTypeEnum,
  TreeTypeEnum,
} from '@/common/services/swagger/index.defs';
import { IStyle } from '@/common/interfaces/IStyle';
import { IProductsInstance } from './IProductsInstance';
import { InstanceType } from '@/common/api/runtime/IInstance';
import { ICommand } from '@/common/api/runtime/ICommand';
import { ProductSelection } from '../../common/api/runtime/ProductSelection';
import { getHelpers } from './runtime/Helpers';
import { IHelpers } from './runtime/IHelpers';
import { defaultRoute } from '../api/configuration/DefaultRoute';

export class Instance implements IProductsInstance {
  public readonly helpers: IHelpers;
  public readonly instanceType: InstanceType = 'Products';
  public readonly props: { [key: string]: any } = {};

  public httpService?: HttpService;
  public readonly store: IStore = reactive({
    notifications: [],
    data: { isInitialized: false, actions: { data: null, current: null } },
    options: { isInitialized: false },
  });
  public readonly router = new Router(this);
  public readonly translations = new Translations(this);
  public readonly commandExecutor = new CommandExecutor(this);
  public readonly logger = LoggerService;

  public readonly registeredComponents = reactive(new Set<ComponentName>());
  public readonly dataTypesRequired = reactive(new Set<DataType>());

  public readonly eventBus = mitt();
  private readonly eventBusExt = mitt();

  public readonly selection = new ProductSelection(this);

  constructor(public id: string, public accessor: Accessor) {
    this.eventBus.on('*', (type, data: unknown) => {
      // dispatch external events, some events are filtered out
      if (!type.toString().startsWith('internal:')) {
        this.eventBusExt.emit(type, data);
      }
    });
    this.helpers = getHelpers(this);
  }

  public isInitialized(): boolean | undefined {
    return this.store.data.isInitialized;
  }

  public async reload(): Promise<void> {
    await this.translations.load();
    await this.router.reload();
  }

  public setRoute(routeData: IRouteData): void {
    this.router.setRoute(routeData);
  }

  public async init(
    optionsOverrides: IOptions,
    routeData?: IRouteData,
    hideInitError?: boolean,
  ): Promise<void> {
    if (this.store.data.isInitialized) {
      throw new Error(`Instance "${this.id}" is already initialized`);
    }

    const currentOptions: IOptions = this.store.options.isInitialized
      ? this.store.options
      : new DefaultOptions();

    _mergeWith(currentOptions, optionsOverrides, (a, b) => (_isArray(b) ? b : undefined));

    if (currentOptions.inheritStyles) {
      currentOptions.styles = [...(currentOptions.styles ?? []), ...this.getDocumentStyles()];
    }

    // merge route reflect passed props and avoid component blinking
    if (routeData) {
      this.router.merge(routeData);
    }

    this.store.options = currentOptions;
    this.store.options.isInitialized = true;

    this.httpService = new HttpService(this, currentOptions.baseUrl, currentOptions.accessToken);

    this.store.data.isInitialized = true;

    this.router.setRoute(routeData);
    try {
      await this.translations.load();
      return Promise.resolve();
    } catch (err) {
      this.store.data.isInitialized = false;
      if (!hideInitError) alert('PIS initialization error.');
      return Promise.reject('PIS initialization error');
    }
  }

  public update(optionsOverrides: IOptions, routeData?: IRouteData): void {
    const currentOptions: IOptions = this.store.options.isInitialized
      ? this.store.options
      : new DefaultOptions();

    _mergeWith(currentOptions, optionsOverrides, (a, b) => (_isArray(b) ? b : undefined));

    if (currentOptions.inheritStyles) {
      currentOptions.styles = [...(currentOptions.styles ?? []), ...this.getDocumentStyles()];
    }

    this.store.options = currentOptions;
    this.store.options.isInitialized = true;

    if (routeData) {
      this.router.setRoute(routeData);
    }
  }

  public on(name: string, handler: (type: string, data: unknown) => void): void {
    this.eventBusExt.on(name, handler as never);
  }

  public onReady(callback: () => any): void {
    const unwatch = watch(
      () => this.store.data,
      ({ isInitialized }) => {
        if (isInitialized) {
          callback();
          unwatch();
        }
      },
      { deep: true },
    );
  }

  public off(name?: string | undefined, handler?: (type: string, data: unknown) => void): void {
    if (name) {
      this.eventBusExt.off(name, handler as never);
    } else {
      this.eventBusExt.all.clear();
      // this.intercept = undefined;
    }
  }

  public registerComponent(componentName: ComponentName): void {
    this.registeredComponents.add(componentName);
  }

  public registerRequiredDataTypes(dataTypes: DataType[]): void {
    dataTypes.forEach((dataType) => this.dataTypesRequired.add(dataType));
  }

  public getRegisteredSearchDataTypes(): SearchDataTypeEnum[] {
    if (this.dataTypesRequired && this.dataTypesRequired.size) {
      const searchDataTypes = Object.values(SearchDataTypeEnum);

      const arr = Array.from(this.dataTypesRequired).filter((dataType) =>
        searchDataTypes.includes(dataType as SearchDataTypeEnum),
      ) as SearchDataTypeEnum[];

      if (this.store.options.components?.configurators?.visible) {
        arr.push(SearchDataTypeEnum.Configurators);
      }

      if (this.store.options.application?.treeType == TreeTypeEnum.Parts)
        arr.push(SearchDataTypeEnum.MasterProducts);

      return _uniq(arr);
    } else {
      return [];
    }
  }

  public getRegisteredDetailsDataTypes(): DetailDataTypeEnum[] {
    if (this.dataTypesRequired && this.dataTypesRequired.size) {
      const detailDataTypes = Object.values(DetailDataTypeEnum);

      const arr = Array.from(this.dataTypesRequired).filter((dataType) =>
        detailDataTypes.includes(dataType as DetailDataTypeEnum),
      ) as DetailDataTypeEnum[];

      // TODO: some options will add data types, for example when configurators are enabled we add the data type

      arr.push(DetailDataTypeEnum.InteractiveGuides);

      if (this.store.options.components?.relatedLinks?.visible) {
        arr.push(DetailDataTypeEnum.RelatedLinks);
      }

      return _uniq(arr);
    } else {
      return [];
    }
  }

  public destroy(): void {
    return this.accessor.destroyInstance(this.id);
  }

  public execute(command: ICommand<Instance>): Promise<unknown> {
    return this.commandExecutor.execute(command);
  }

  public getDocumentStyles(): IStyle[] {
    return [...document.styleSheets].map((sheet) => {
      if (sheet.href) {
        return { url: sheet.href };
      } else {
        try {
          return { css: [...sheet.cssRules].map((rule) => rule.cssText).join('') };
        } catch (e) {
          return {};
        }
      }
    });
  }

  public getDefaultOptions(): IOptions {
    return new DefaultOptions();
  }

  public getDefaultRouteData(): IRouteData {
    return _cloneDeep(defaultRoute);
  }

  public getOptions(): IOptions {
    return this.store.options;
  }

  public getRouteData(): IRouteData {
    if (this.props['nextRoute']) {
      return this.props['nextRoute'];
    }

    return _cloneDeep(this.router.routeData);
  }
}
